export interface FoodAlternative {
  name: string;
  gi: number;
  giLevel: "low" | "medium" | "high";
  reason: string;
}

export interface FoodRecommendation {
  originalFood: string;
  originalGI?: number;
  originalLevel?: "low" | "medium" | "high";
  alternatives: FoodAlternative[];
  tip?: string;
}

// Common food searches mapped to lower-GI alternatives
const FOOD_ALTERNATIVES: Record<string, FoodRecommendation> = {
  // Breads
  bread: {
    originalFood: "bread",
    originalGI: 75,
    originalLevel: "high",
    alternatives: [
      { name: "Sourdough bread", gi: 54, giLevel: "low", reason: "Fermentation lowers GI" },
      { name: "Rye bread", gi: 58, giLevel: "medium", reason: "Higher fiber content" },
      { name: "Whole grain bread", gi: 51, giLevel: "low", reason: "Intact grains slow digestion" },
    ],
    tip: "Toast your bread - it slightly lowers the GI!",
  },
  "white bread": {
    originalFood: "white bread",
    originalGI: 75,
    originalLevel: "high",
    alternatives: [
      { name: "Sourdough bread", gi: 54, giLevel: "low", reason: "Fermentation lowers GI" },
      { name: "Pumpernickel", gi: 50, giLevel: "low", reason: "Dense, whole grain" },
      { name: "Ezekiel bread", gi: 36, giLevel: "low", reason: "Sprouted grains" },
    ],
  },

  // Rice
  rice: {
    originalFood: "rice",
    originalGI: 73,
    originalLevel: "high",
    alternatives: [
      { name: "Basmati rice", gi: 58, giLevel: "medium", reason: "Long grain, lower GI" },
      { name: "Quinoa", gi: 53, giLevel: "low", reason: "Complete protein, lower GI" },
      { name: "Cauliflower rice", gi: 15, giLevel: "low", reason: "Very low carb alternative" },
    ],
    tip: "Cooking rice and cooling it creates resistant starch, lowering GI by 10-15%",
  },
  "white rice": {
    originalFood: "white rice",
    originalGI: 73,
    originalLevel: "high",
    alternatives: [
      { name: "Basmati rice", gi: 58, giLevel: "medium", reason: "Same taste, lower GI" },
      { name: "Brown rice", gi: 68, giLevel: "medium", reason: "More fiber and nutrients" },
      { name: "Wild rice", gi: 45, giLevel: "low", reason: "Technically a grass, much lower GI" },
    ],
  },

  // Pasta
  pasta: {
    originalFood: "pasta",
    originalGI: 55,
    originalLevel: "low",
    alternatives: [
      { name: "Al dente pasta", gi: 45, giLevel: "low", reason: "Less cooking = lower GI" },
      { name: "Whole wheat pasta", gi: 42, giLevel: "low", reason: "More fiber" },
      { name: "Chickpea pasta", gi: 35, giLevel: "low", reason: "High protein, low GI" },
    ],
    tip: "Add olive oil and eat with protein to further reduce blood sugar impact",
  },

  // Potatoes
  potato: {
    originalFood: "potato",
    originalGI: 85,
    originalLevel: "high",
    alternatives: [
      { name: "Sweet potato (boiled)", gi: 44, giLevel: "low", reason: "Nearly half the GI" },
      { name: "New potatoes", gi: 57, giLevel: "medium", reason: "Waxy variety, lower GI" },
      { name: "Cauliflower mash", gi: 15, giLevel: "low", reason: "Very low carb swap" },
    ],
    tip: "Cook potatoes, cool them completely, then reheat - creates resistant starch",
  },
  potatoes: {
    originalFood: "potatoes",
    originalGI: 85,
    originalLevel: "high",
    alternatives: [
      { name: "Sweet potatoes", gi: 44, giLevel: "low", reason: "Nearly half the GI" },
      { name: "Lentils", gi: 32, giLevel: "low", reason: "High protein, very low GI" },
      { name: "Butternut squash", gi: 51, giLevel: "low", reason: "Sweet, lower GI" },
    ],
  },

  // Sugar & Sweeteners
  sugar: {
    originalFood: "sugar",
    originalGI: 65,
    originalLevel: "medium",
    alternatives: [
      { name: "Coconut sugar", gi: 35, giLevel: "low", reason: "Lower GI, similar taste" },
      { name: "Stevia", gi: 0, giLevel: "low", reason: "Zero glycemic impact" },
      { name: "Monk fruit", gi: 0, giLevel: "low", reason: "Natural, zero GI" },
    ],
  },

  // Snacks
  chips: {
    originalFood: "chips",
    originalGI: 75,
    originalLevel: "high",
    alternatives: [
      { name: "Popcorn", gi: 65, giLevel: "medium", reason: "Whole grain, more filling" },
      { name: "Veggie chips (baked)", gi: 45, giLevel: "low", reason: "Lower carb option" },
      { name: "Nuts", gi: 14, giLevel: "low", reason: "Protein and healthy fats" },
    ],
  },

  // Breakfast
  cereal: {
    originalFood: "cereal",
    originalGI: 81,
    originalLevel: "high",
    alternatives: [
      { name: "Steel-cut oats", gi: 42, giLevel: "low", reason: "Minimally processed" },
      { name: "All-Bran", gi: 38, giLevel: "low", reason: "High fiber" },
      { name: "Muesli (no sugar)", gi: 49, giLevel: "low", reason: "Whole grains and nuts" },
    ],
    tip: "Add protein (Greek yogurt, nuts) to any breakfast to lower the overall GI",
  },
  oatmeal: {
    originalFood: "oatmeal",
    originalGI: 55,
    originalLevel: "low",
    alternatives: [
      { name: "Steel-cut oats", gi: 42, giLevel: "low", reason: "Less processed, lower GI" },
      { name: "Overnight oats", gi: 40, giLevel: "low", reason: "Resistant starch forms overnight" },
    ],
    tip: "Add cinnamon - it may help with insulin sensitivity",
  },

  // Fruits
  banana: {
    originalFood: "banana",
    originalGI: 51,
    originalLevel: "low",
    alternatives: [
      { name: "Green banana", gi: 30, giLevel: "low", reason: "Less ripe = more resistant starch" },
      { name: "Apple", gi: 36, giLevel: "low", reason: "High fiber, lower GI" },
      { name: "Berries", gi: 25, giLevel: "low", reason: "Lowest GI fruits" },
    ],
    tip: "Pair bananas with nut butter to slow sugar absorption",
  },

  // Drinks
  soda: {
    originalFood: "soda",
    originalGI: 63,
    originalLevel: "medium",
    alternatives: [
      { name: "Sparkling water", gi: 0, giLevel: "low", reason: "Zero sugar, zero GI" },
      { name: "Unsweetened iced tea", gi: 0, giLevel: "low", reason: "Zero GI with antioxidants" },
      { name: "Diet soda", gi: 0, giLevel: "low", reason: "Zero sugar (artificial sweeteners)" },
    ],
  },
  juice: {
    originalFood: "juice",
    originalGI: 50,
    originalLevel: "low",
    alternatives: [
      { name: "Whole fruit", gi: 36, giLevel: "low", reason: "Fiber slows absorption" },
      { name: "Vegetable juice", gi: 43, giLevel: "low", reason: "Lower sugar content" },
      { name: "Infused water", gi: 0, giLevel: "low", reason: "Zero calories, zero GI" },
    ],
    tip: "Eating whole fruit is almost always better than drinking juice",
  },

  // Other common searches
  pizza: {
    originalFood: "pizza",
    originalGI: 60,
    originalLevel: "medium",
    alternatives: [
      { name: "Cauliflower crust pizza", gi: 25, giLevel: "low", reason: "Very low carb base" },
      { name: "Thin crust pizza", gi: 50, giLevel: "low", reason: "Less dough = lower GI" },
    ],
    tip: "Load up on veggie toppings and cheese to slow down carb absorption",
  },
  chocolate: {
    originalFood: "chocolate",
    originalGI: 49,
    originalLevel: "low",
    alternatives: [
      { name: "Dark chocolate (70%+)", gi: 23, giLevel: "low", reason: "Less sugar, more cocoa" },
      { name: "Dark chocolate (85%+)", gi: 20, giLevel: "low", reason: "Minimal sugar" },
    ],
    tip: "The darker the chocolate, the lower the GI - aim for 70% cocoa or higher",
  },
  ice: {
    originalFood: "ice cream",
    originalGI: 51,
    originalLevel: "low",
    alternatives: [
      { name: "Greek yogurt with berries", gi: 15, giLevel: "low", reason: "High protein, low sugar" },
      { name: "Frozen banana blend", gi: 35, giLevel: "low", reason: "Natural sweetness" },
      { name: "Halo Top", gi: 35, giLevel: "low", reason: "Low sugar ice cream" },
    ],
  },
  "ice cream": {
    originalFood: "ice cream",
    originalGI: 51,
    originalLevel: "low",
    alternatives: [
      { name: "Greek yogurt parfait", gi: 15, giLevel: "low", reason: "Protein-rich alternative" },
      { name: "Frozen berries with cream", gi: 25, giLevel: "low", reason: "Natural, lower sugar" },
    ],
  },
};

// Normalize food name for lookup
function normalizeFood(food: string): string {
  return food.toLowerCase().trim();
}

// Get recommendation for a specific food
export function getRecommendation(food: string): FoodRecommendation | null {
  const normalized = normalizeFood(food);

  // Direct match
  if (FOOD_ALTERNATIVES[normalized]) {
    return FOOD_ALTERNATIVES[normalized];
  }

  // Partial match - check if any key contains or is contained in the search
  for (const [key, rec] of Object.entries(FOOD_ALTERNATIVES)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return rec;
    }
  }

  return null;
}

// Get recommendations for multiple foods
export function getRecommendations(foods: { food: string; count: number }[]): FoodRecommendation[] {
  const recommendations: FoodRecommendation[] = [];

  for (const { food } of foods) {
    const rec = getRecommendation(food);
    if (rec && !recommendations.some((r) => r.originalFood === rec.originalFood)) {
      recommendations.push(rec);
    }
  }

  return recommendations;
}

// Common foods to detect in messages
export const DETECTABLE_FOODS = [
  "bread", "white bread", "sourdough", "toast",
  "rice", "white rice", "brown rice", "basmati", "jasmine",
  "pasta", "spaghetti", "noodles",
  "potato", "potatoes", "fries", "mashed",
  "sugar", "honey", "syrup",
  "chips", "crisps", "crackers",
  "cereal", "oatmeal", "oats", "granola",
  "banana", "apple", "orange", "fruit",
  "soda", "cola", "juice", "drink",
  "pizza", "burger", "sandwich",
  "chocolate", "candy", "cookie", "cake",
  "ice cream", "dessert",
  "chicken", "beef", "fish", "salmon",
  "eggs", "cheese", "yogurt", "milk",
];

// Extract food mentions from a message
export function extractFoodMentions(message: string): string[] {
  const normalized = message.toLowerCase();
  const found: string[] = [];

  for (const food of DETECTABLE_FOODS) {
    // Use word boundary matching to avoid partial matches
    const regex = new RegExp(`\\b${food}s?\\b`, "i");
    if (regex.test(normalized) && !found.includes(food)) {
      found.push(food);
    }
  }

  return found;
}
